/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useCompliance.ts
 * PURPOSE: React Hook for Soft Compliance Validation and Override
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (React Best Practices)
 * SPRINT GOVERNANCE: Rule 9: Documentation Standards
 *
 * DATE: 2025-12-26
 * COMPLIANCE: SDLC 6.3.1 + Expert 3 Feedback + Vietnamese Labor Law 2019
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Compliance
 * - Impact: Enable soft compliance with manager override in UI
 * - Stakeholders: Branch Managers, HR Directors, Operations
 *
 * @technical-architecture
 * - Layer: Presentation Layer, React Hooks
 * - Pattern: Custom Hook, State Management
 * - Dependencies: React, complianceService
 *
 * @cross-references
 * - Service: frontend/src/services/api/complianceService.ts
 * - Design: docs/02-design/05-business-logic/SOFT-COMPLIANCE-WARNING-DESIGN.md
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  complianceService,
  ComplianceValidationRequest,
  ComplianceValidationResponse,
  OverrideRequest,
  OverrideResponse,
  OverrideAuditLog,
  AuditLogListResponse,
  OverrideStatistics,
  ComplianceSeverity,
  OverrideStatus,
  ViolationType,
  OverrideReasonCategory,
} from '../services/api/complianceService';

// ============= Types =============

export interface UseComplianceState {
  // Validation state
  validationResult: ComplianceValidationResponse | null;
  isValidating: boolean;
  validationError: string | null;

  // Override state
  overrideResult: OverrideResponse | null;
  isOverriding: boolean;
  overrideError: string | null;

  // Audit logs state
  auditLogs: AuditLogListResponse | null;
  isLoadingLogs: boolean;
  logsError: string | null;

  // Statistics state
  statistics: OverrideStatistics | null;
  isLoadingStats: boolean;
  statsError: string | null;
}

export interface UseComplianceActions {
  validateCompliance: (request: ComplianceValidationRequest) => Promise<ComplianceValidationResponse | null>;
  requestOverride: (request: OverrideRequest) => Promise<OverrideResponse | null>;
  fetchAuditLogs: (params: {
    employee_id?: string;
    date_from?: string;
    date_to?: string;
    severity?: ComplianceSeverity;
    status?: OverrideStatus;
    page?: number;
    page_size?: number;
  }) => Promise<AuditLogListResponse | null>;
  fetchStatistics: (params: {
    date_from?: string;
    date_to?: string;
  }) => Promise<OverrideStatistics | null>;
  clearValidation: () => void;
  clearOverride: () => void;
}

export interface UseComplianceReturn extends UseComplianceState, UseComplianceActions {
  // Computed values
  hasBlockingViolations: boolean;
  hasOverridableViolations: boolean;
  canProceed: boolean;
}

// ============= Hook Implementation =============

export function useCompliance(): UseComplianceReturn {
  // Validation state
  const [validationResult, setValidationResult] = useState<ComplianceValidationResponse | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Override state
  const [overrideResult, setOverrideResult] = useState<OverrideResponse | null>(null);
  const [isOverriding, setIsOverriding] = useState(false);
  const [overrideError, setOverrideError] = useState<string | null>(null);

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState<AuditLogListResponse | null>(null);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);

  // Statistics state
  const [statistics, setStatistics] = useState<OverrideStatistics | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Validate compliance
  const validateCompliance = useCallback(
    async (request: ComplianceValidationRequest): Promise<ComplianceValidationResponse | null> => {
      setIsValidating(true);
      setValidationError(null);

      try {
        const result = await complianceService.validateCompliance(request);
        setValidationResult(result);
        return result;
      } catch (error: any) {
        const errorMessage = error.response?.data?.detail || error.message || 'Validation failed';
        setValidationError(errorMessage);
        return null;
      } finally {
        setIsValidating(false);
      }
    },
    []
  );

  // Request override
  const requestOverride = useCallback(
    async (request: OverrideRequest): Promise<OverrideResponse | null> => {
      setIsOverriding(true);
      setOverrideError(null);

      try {
        const result = await complianceService.requestOverride(request);
        setOverrideResult(result);
        return result;
      } catch (error: any) {
        const errorMessage = error.response?.data?.detail || error.message || 'Override request failed';
        setOverrideError(errorMessage);
        return null;
      } finally {
        setIsOverriding(false);
      }
    },
    []
  );

  // Fetch audit logs
  const fetchAuditLogs = useCallback(
    async (params: {
      employee_id?: string;
      date_from?: string;
      date_to?: string;
      severity?: ComplianceSeverity;
      status?: OverrideStatus;
      page?: number;
      page_size?: number;
    }): Promise<AuditLogListResponse | null> => {
      setIsLoadingLogs(true);
      setLogsError(null);

      try {
        const result = await complianceService.getAuditLogs(params);
        setAuditLogs(result);
        return result;
      } catch (error: any) {
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch audit logs';
        setLogsError(errorMessage);
        return null;
      } finally {
        setIsLoadingLogs(false);
      }
    },
    []
  );

  // Fetch statistics
  const fetchStatistics = useCallback(
    async (params: {
      date_from?: string;
      date_to?: string;
    }): Promise<OverrideStatistics | null> => {
      setIsLoadingStats(true);
      setStatsError(null);

      try {
        const result = await complianceService.getOverrideStatistics(params);
        setStatistics(result);
        return result;
      } catch (error: any) {
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch statistics';
        setStatsError(errorMessage);
        return null;
      } finally {
        setIsLoadingStats(false);
      }
    },
    []
  );

  // Clear validation
  const clearValidation = useCallback(() => {
    setValidationResult(null);
    setValidationError(null);
  }, []);

  // Clear override
  const clearOverride = useCallback(() => {
    setOverrideResult(null);
    setOverrideError(null);
  }, []);

  // Computed values
  const hasBlockingViolations = useMemo(() => {
    return (validationResult?.blocking_violations?.length ?? 0) > 0;
  }, [validationResult]);

  const hasOverridableViolations = useMemo(() => {
    return (validationResult?.overridable_violations?.length ?? 0) > 0;
  }, [validationResult]);

  const canProceed = useMemo(() => {
    if (!validationResult) return true;
    return validationResult.is_compliant || !hasBlockingViolations;
  }, [validationResult, hasBlockingViolations]);

  return {
    // State
    validationResult,
    isValidating,
    validationError,
    overrideResult,
    isOverriding,
    overrideError,
    auditLogs,
    isLoadingLogs,
    logsError,
    statistics,
    isLoadingStats,
    statsError,

    // Actions
    validateCompliance,
    requestOverride,
    fetchAuditLogs,
    fetchStatistics,
    clearValidation,
    clearOverride,

    // Computed
    hasBlockingViolations,
    hasOverridableViolations,
    canProceed,
  };
}

// ============= Utility Hook for Single Validation =============

export function useComplianceValidation(
  request: ComplianceValidationRequest | null,
  autoValidate: boolean = false
) {
  const { validateCompliance, validationResult, isValidating, validationError, clearValidation } =
    useCompliance();

  // Auto-validate when request changes
  useEffect(() => {
    if (!autoValidate || !request) return;
    void validateCompliance(request);
  }, [autoValidate, request, validateCompliance]);

  return {
    validate: () => (request ? validateCompliance(request) : Promise.resolve(null)),
    result: validationResult,
    isValidating,
    error: validationError,
    clear: clearValidation,
  };
}

// ============= Export =============

export default useCompliance;
